import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { FTUEState, FTUEStep, FTUEContextType } from '../types/ftue.types';

// Initial state
const initialState: FTUEState = {
  isActive: false,
  currentStepIndex: 0,
  steps: [],
  isCompleted: false,
};

// Action types
type FTUEAction =
  | { type: 'START_FTUE'; payload: FTUEStep[] }
  | { type: 'NEXT_STEP' }
  | { type: 'SKIP_FTUE' }
  | { type: 'RESET_FTUE' }
  | { type: 'SET_STEPS'; payload: FTUEStep[] };

// Reducer
function ftueReducer(state: FTUEState, action: FTUEAction): FTUEState {
  switch (action.type) {
    case 'START_FTUE':
      return {
        ...state,
        isActive: true,
        currentStepIndex: 0,
        steps: action.payload,
        isCompleted: false,
      };
    
    case 'NEXT_STEP':
      const nextIndex = state.currentStepIndex + 1;
      if (nextIndex >= state.steps.length) {
        return {
          ...state,
          isActive: false,
          isCompleted: true,
        };
      }
      return {
        ...state,
        currentStepIndex: nextIndex,
      };
    
    case 'SKIP_FTUE':
      return {
        ...state,
        isActive: false,
        isCompleted: true,
      };
    
    case 'RESET_FTUE':
      return initialState;
    
    case 'SET_STEPS':
      return {
        ...state,
        steps: action.payload,
      };
    
    default:
      return state;
  }
}

// Context
const FTUEContext = createContext<FTUEContextType | undefined>(undefined);

// Provider component
export const FTUEProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(ftueReducer, initialState);

  const startFTUE = (steps: FTUEStep[]) => {
    dispatch({ type: 'START_FTUE', payload: steps });
  };

  const nextStep = () => {
    dispatch({ type: 'NEXT_STEP' });
  };

  const skipFTUE = () => {
    dispatch({ type: 'SKIP_FTUE' });
  };

  const resetFTUE = () => {
    dispatch({ type: 'RESET_FTUE' });
  };

  const setSteps = (steps: FTUEStep[]) => {
    dispatch({ type: 'SET_STEPS', payload: steps });
  };

  const value: FTUEContextType = {
    state,
    startFTUE,
    nextStep,
    skipFTUE,
    resetFTUE,
    setSteps,
  };

  return (
    <FTUEContext.Provider value={value}>
      {children}
    </FTUEContext.Provider>
  );
};

// Custom hook
export const useFTUE = () => {
  const context = useContext(FTUEContext);
  if (context === undefined) {
    throw new Error('useFTUE must be used within a FTUEProvider');
  }
  return context;
};
